import React, { useEffect, useState, useCallback } from "react";
import * as func from "../../api/apiHelper";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoltLightning,
  faSpinner,
  faPlus,
  faPencil,
  faTrash,
  faTimes,
  faToggleOn,
  faToggleOff,
} from "@fortawesome/free-solid-svg-icons";

const EVENTS = [
  { value: "no_connection_3d",    label: "No Connection (3 days)",  color: "bg-orange-100 text-orange-700" },
  { value: "interest_accepted",   label: "Interest Accepted",       color: "bg-green-100 text-green-700" },
  { value: "inactive_14d",        label: "Inactive (14 days)",      color: "bg-red-100 text-red-600" },
  { value: "never_paid",          label: "Never Paid",              color: "bg-yellow-100 text-yellow-700" },
  { value: "profile_incomplete",  label: "Profile Incomplete",      color: "bg-blue-100 text-blue-700" },
];

const eventMeta = (event) => EVENTS.find(e => e.value === event) ?? { label: event, color: "bg-gray-100 text-gray-600" };

const emptyForm = () => ({
  name: "",
  description: "",
  event: "no_connection_3d",
  title: "",
  body: "",
  deepLink: "",
});

const TriggerModal = ({ initial, onSave, onClose }) => {
  const [form, setForm] = useState(initial ? { ...initial } : emptyForm());
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name || !form.event || !form.title || !form.body) {
      toast.warn("Name, event, title, and body are required.");
      return;
    }
    setSaving(true);
    await onSave(form);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
        <div className="flex justify-between items-center p-5 border-b">
          <h2 className="font-semibold text-lg text-gray-800">{initial ? "Edit Trigger" : "New Trigger"}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><FontAwesomeIcon icon={faTimes} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Name</label>
            <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. Re-engage 3-day idle users" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Event Trigger</label>
            <select className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.event} onChange={(e) => set("event", e.target.value)}>
              {EVENTS.map(e => <option key={e.value} value={e.value}>{e.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Notification Title</label>
            <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="Your match is waiting!" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Notification Body</label>
            <textarea rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.body} onChange={(e) => set("body", e.target.value)} placeholder="You have new matches waiting for you. Tap to explore." />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Deep Link (optional)</label>
            <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.deepLink ?? ""} onChange={(e) => set("deepLink", e.target.value)} placeholder="matchmatrix://discover" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Description (optional)</label>
            <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.description ?? ""} onChange={(e) => set("description", e.target.value)} />
          </div>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 border rounded-lg hover:bg-gray-50">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2">
            {saving && <FontAwesomeIcon icon={faSpinner} spin />}
            Save Trigger
          </button>
        </div>
      </div>
    </div>
  );
};

const NotificationTriggers = () => {
  const [loading, setLoading] = useState(true);
  const [triggers, setTriggers] = useState([]);
  const [modal, setModal] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [toggling, setToggling] = useState(null);

  const fetchTriggers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await func.get("mst-admin/notifications/triggers");
      if (res?.status === 200 && res.data?.success) {
        setTriggers(res.data.triggers || []);
      }
    } catch {
      toast.error("Failed to load triggers");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchTriggers(); }, [fetchTriggers]);

  const handleSave = async (form) => {
    try {
      let res;
      if (modal.mode === "edit") {
        res = await func.put(`mst-admin/notifications/triggers/${modal.data._id}`, form);
      } else {
        res = await func.post("mst-admin/notifications/triggers", form);
      }
      if (res?.status === 200 || res?.status === 201) {
        toast.success(modal.mode === "edit" ? "Trigger updated" : "Trigger created");
        setModal(null);
        fetchTriggers();
      } else {
        toast.error(res?.data?.message || "Save failed");
      }
    } catch {
      toast.error("Save failed");
    }
  };

  const handleToggle = async (trigger) => {
    setToggling(trigger._id);
    try {
      const res = await func.put(`mst-admin/notifications/triggers/${trigger._id}`, { active: !trigger.active });
      if (res?.status === 200 && res.data?.success) {
        toast.success(`Trigger ${!trigger.active ? "enabled" : "disabled"}`);
        fetchTriggers();
      } else {
        toast.error("Toggle failed");
      }
    } catch {
      toast.error("Toggle failed");
    }
    setToggling(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this trigger? It will no longer fire.")) return;
    setDeleting(id);
    try {
      const res = await func.del(`mst-admin/notifications/triggers/${id}`);
      if (res?.status === 200) {
        toast.success("Trigger deleted");
        fetchTriggers();
      } else {
        toast.error("Delete failed");
      }
    } catch {
      toast.error("Delete failed");
    }
    setDeleting(null);
  };

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "Never";

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faBoltLightning} className="text-2xl text-indigo-600" />
          <h1 className="text-2xl font-bold text-gray-800">Notification Triggers</h1>
        </div>
        <button
          onClick={() => setModal({ mode: "create" })}
          className="flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-indigo-700"
        >
          <FontAwesomeIcon icon={faPlus} />
          New Trigger
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <FontAwesomeIcon icon={faSpinner} spin className="text-3xl text-gray-400" />
        </div>
      ) : triggers.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <FontAwesomeIcon icon={faBoltLightning} className="text-4xl mb-3" />
          <p>No triggers yet. Create your first automated notification.</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {triggers.map((t) => {
            const meta = eventMeta(t.event);
            return (
              <div key={t._id} className={`bg-white rounded-2xl border shadow-sm p-5 ${!t.active ? "opacity-60" : ""}`}>
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <p className="font-semibold text-gray-800">{t.name}</p>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${meta.color}`}>{meta.label}</span>
                      {!t.active && <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-500">Disabled</span>}
                    </div>
                    {t.description && <p className="text-xs text-gray-400 mb-2">{t.description}</p>}
                    <div className="bg-gray-50 rounded-xl p-3 space-y-1">
                      <p className="text-sm font-medium text-gray-800">📣 {t.title}</p>
                      <p className="text-xs text-gray-600">{t.body}</p>
                      {t.deepLink && <p className="text-xs text-blue-500 font-mono">{t.deepLink}</p>}
                    </div>
                    <div className="flex gap-4 mt-2 text-xs text-gray-400">
                      <span>Last run: {fmtDate(t.lastRunAt)}</span>
                      <span>Total sent: {(t.totalSent || 0).toLocaleString()}</span>
                    </div>
                  </div>
                  <div className="flex gap-3 items-center shrink-0">
                    <button
                      onClick={() => handleToggle(t)}
                      disabled={toggling === t._id}
                      className={`text-xl ${t.active ? "text-green-500" : "text-gray-300"} hover:opacity-80 disabled:opacity-50`}
                    >
                      <FontAwesomeIcon icon={toggling === t._id ? faSpinner : (t.active ? faToggleOn : faToggleOff)} spin={toggling === t._id} />
                    </button>
                    <button onClick={() => setModal({ mode: "edit", data: t })} className="text-blue-500 hover:text-blue-700">
                      <FontAwesomeIcon icon={faPencil} />
                    </button>
                    <button
                      onClick={() => handleDelete(t._id)}
                      disabled={deleting === t._id}
                      className="text-red-400 hover:text-red-600 disabled:opacity-50"
                    >
                      <FontAwesomeIcon icon={deleting === t._id ? faSpinner : faTrash} spin={deleting === t._id} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {modal && (
        <TriggerModal initial={modal.data} onSave={handleSave} onClose={() => setModal(null)} />
      )}
    </div>
  );
};

export default NotificationTriggers;
